import {useCallback, useEffect, useMemo, useState} from 'react'
import {useApolloClient} from '@apollo/client'
import {Alert, Badge, Button, Form, Modal, Pagination, Spinner, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {UPDATE_POLICIES_BY_CARRIER_STR} from '../../../gql/mutations/policyMutation'
import {GET_ALL_CARRIERS_WITH_STATUS_LIST} from '../../../gql/queries/carriersQuery'
import {DateInput} from 'app/tfi/components/DateInput'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {exportUnmatchedCsv, CarrierImportUnmatchedRow} from './carrierImportExport'
import {parseId} from 'app/tfi/helpers'

interface CarrierImportResult {
  updatePoliciesByCarrier: {
    success: boolean
    message: string
    processedCount: number
    matchedCount: number
    unmatchedCount: number
    errorsCount: number
    unmatchedRows: CarrierImportUnmatchedRow[]
  }
}

interface CarrierItem {
  id: string
  name: string
  enabled?: boolean
}

interface PolicyStatusItem {
  code: string
  title: string
  color?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  carriers: CarrierItem[]
  policyStatuses: PolicyStatusItem[]
}

const SUPPORTED_CARRIERS = ['ambetter', 'oscar', 'blue cross', 'bcbs', 'molina', 'anthem', 'imperial', 'sentara', 'jefferson', 'independence', 'amerihealth', 'ameri health', 'cigna', 'wellpoint', 'alliant']
const ANTHEM_CARRIER_FALLBACK: CarrierItem = {id: '17', name: 'Anthem', enabled: false}
const PAGE_SIZE = 25

export const ModalUpdatePoliciesByCarrier: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  carriers,
  policyStatuses,
}) => {
  const [carrierId, setCarrierId] = useState('')
  const [asOfDate, setAsOfDate] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [fetchedCarriers, setFetchedCarriers] = useState<CarrierItem[]>([])
  const [loadingCarriers, setLoadingCarriers] = useState(false)

  const [uploadFn, {data, loading: isUploading, error}] = useUploadFile<CarrierImportResult>()

  const client = useApolloClient()

  useEffect(() => {
    if (!isOpen) return

    setLoadingCarriers(true)
    const searchTerms = ['ambetter', 'oscar', 'blue cross', 'bcbs', 'molina', 'anthem', 'imperial', 'sentara', 'jefferson', 'independence', 'amerihealth', 'cigna', 'wellpoint', 'alliant', 'united']

    Promise.all(
      searchTerms.map((name) =>
        client.query({
          query: GET_ALL_CARRIERS_WITH_STATUS_LIST,
          variables: {status: 'all', name, first: 20},
          fetchPolicy: 'network-only',
        })
      )
    )
      .then((results) => {
        const seen = new Set<string>()
        const merged: CarrierItem[] = []
        for (const result of results) {
          const edges = result.data?.allInsuranceOperators?.edges || []
          for (const edge of edges) {
            const node = edge?.node
            if (node && !seen.has(String(node.id))) {
              seen.add(String(node.id))
              merged.push(node)
            }
          }
        }
        setFetchedCarriers(merged)
      })
      .catch(() => {})
      .finally(() => setLoadingCarriers(false))
  }, [isOpen, client])

  const allCarriers: CarrierItem[] = useMemo(() => {
    const base = fetchedCarriers.length > 0 ? fetchedCarriers : carriers || []
    const hasAnthem = base.some((carrier) => {
      const name = carrier?.name?.trim().toLowerCase() || ''
      return String(carrier?.id) === ANTHEM_CARRIER_FALLBACK.id || name === 'anthem'
    })
    if (hasAnthem) return base
    return [...base, ANTHEM_CARRIER_FALLBACK]
  }, [carriers, fetchedCarriers])

  const supportedCarriers = useMemo(
    () =>
      allCarriers
        .filter((carrier) => {
          const name = carrier?.name?.toLowerCase() || ''
          if (name.includes('capital blue cross')) return false
          if (name.includes('cigna') && name !== 'cigna') return false
          if ((name.includes('amerihealth') || name.includes('ameri health')) && name.includes('caritas')) {
            return false
          }
          if (name === 'united healthcare') return true
          if (name === 'cigna') return true
          return SUPPORTED_CARRIERS.some((sc) => name.includes(sc))
        })
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [allCarriers]
  )

  const statusTitleByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.title || ''
    }
    return map
  }, [policyStatuses])

  const statusColorByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.color || 'secondary'
    }
    return map
  }, [policyStatuses])

  const handleUpload = useCallback(() => {
    if (!carrierId || !asOfDate || !file) {
      toast.error('Please fill all required fields')
      return
    }

    const formData = new FormData()
    formData.append('0', file, file.name)

    const operations = {
      query: UPDATE_POLICIES_BY_CARRIER_STR,
      variables: {
        carrierId: parseId(carrierId),
        asOfDate,
        file: null,
      },
    }
    const map = {'0': ['variables.file']}

    formData.append('operations', JSON.stringify(operations))
    formData.append('map', JSON.stringify(map))

    uploadFn(formData)
  }, [carrierId, asOfDate, file, uploadFn])

  const importResult = data?.updatePoliciesByCarrier

  const handleClose = () => {
    setCarrierId('')
    setAsOfDate('')
    setFile(null)
    setSearchQuery('')
    setCurrentPage(1)
    if (importResult?.success) {
      onSuccess()
    }
    onClose()
  }

  // Filter unmatched rows by search query
  const filteredRows = useMemo(() => {
    const rows = importResult?.unmatchedRows ?? []
    if (!searchQuery.trim()) return rows
    const q = searchQuery.trim().toLowerCase()
    return rows.filter(
      (row) =>
        (row.memberId && row.memberId.toLowerCase().includes(q)) ||
        (row.name && row.name.toLowerCase().includes(q))
    )
  }, [importResult?.unmatchedRows, searchQuery])

  // Pagination
  const totalFiltered = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  // Reset page when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  // Build pagination items
  const paginationItems = useMemo(() => {
    const items: JSX.Element[] = []
    const maxVisible = 5
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2))
    const end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1)
    }
    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={p === safePage} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      )
    }
    return items
  }, [safePage, totalPages])

  return (
    <Modal show={isOpen} onHide={handleClose} size='xl' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Update Policies by Carrier</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!importResult ? (
          <>
            <Form.Group className='mb-3'>
              <Form.Label>Carrier</Form.Label>
              <Form.Select
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
                disabled={isUploading || loadingCarriers}
              >
                <option value=''>
                  {loadingCarriers ? 'Loading carriers...' : 'Select carrier...'}
                </option>
                {supportedCarriers.map((carrier) => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className='text-muted'>
                Supported carriers: Ambetter, Oscar, BCBS, Molina, Anthem, Imperial, Sentara, United
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <DateInput
                id='asOfDate'
                name='asOfDate'
                label='As-of Date'
                value={asOfDate || null}
                disabled={isUploading}
                onChange={(_field, value) => setAsOfDate(value ?? '')}
              />
              <Form.Text className='text-muted'>
                The reference date for this carrier data snapshot
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>File (CSV)</Form.Label>
              <Form.Control
                type='file'
                accept='.csv,.xlsx,.xls'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const f = e.target.files?.[0] || null
                  setFile(f)
                }}
                disabled={isUploading}
              />
            </Form.Group>

            {error && (
              <Alert variant='danger'>
                Error: {typeof error === 'string' ? error : 'Upload failed'}
              </Alert>
            )}
          </>
        ) : (
          <>
            <Alert variant={importResult.success ? 'success' : 'danger'}>
              {importResult.message}
            </Alert>

            <div className='d-flex gap-3 mb-3'>
              <div>
                <strong>Processed:</strong>{' '}
                <Badge bg='primary' text='light'>{importResult.processedCount}</Badge>
              </div>
              <div>
                <strong>Matched:</strong>{' '}
                <Badge bg='success'>{importResult.matchedCount}</Badge>
              </div>
              <div>
                <strong>Unmatched:</strong>{' '}
                <Badge bg='warning' text='dark'>
                  {importResult.unmatchedCount}
                </Badge>
              </div>
              <div>
                <strong>Errors:</strong>{' '}
                <Badge bg='danger'>{importResult.errorsCount}</Badge>
              </div>
            </div>

            {importResult.unmatchedRows && importResult.unmatchedRows.length > 0 && (
              <>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='mb-0'>
                    Unmatched Rows ({totalFiltered}
                    {searchQuery.trim() ? ` of ${importResult.unmatchedRows.length}` : ''})
                  </h6>
                  <Button
                    variant='outline-primary'
                    size='sm'
                    onClick={() =>
                      exportUnmatchedCsv(importResult.unmatchedRows, statusTitleByCode)
                    }
                  >
                    Export Unmatched CSV
                  </Button>
                </div>

                <Form.Control
                  type='text'
                  placeholder='Search by Member ID or Name...'
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='mb-2'
                  size='sm'
                />

                <Table size='sm' striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Member ID</th>
                      <th>Name</th>
                      <th>Carrier Status</th>
                      <th>System Status</th>
                      <th>Agent</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.rowNumber}</td>
                        <td>{row.memberId || '-'}</td>
                        <td>{row.name || '-'}</td>
                        <td>{row.carrierStatusRaw || row.carrierStatusGeneric || '-'}</td>
                        <td>
                          {row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus] ? (
                            <BadgeStatePolicy
                              status={statusTitleByCode[row.systemPolicyStatus]}
                              color={statusColorByCode[row.systemPolicyStatus] || 'secondary'}
                            />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {row.systemAgentName || row.systemAgentEmail ? (
                            <>
                              {row.systemAgentName || '-'}
                              {row.systemAgentEmail ? ` (${row.systemAgentEmail})` : ''}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center'>
                    <small className='text-muted'>
                      Page {safePage} of {totalPages}
                    </small>
                    <Pagination size='sm' className='mb-0'>
                      <Pagination.First
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(1)}
                      />
                      <Pagination.Prev
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                      />
                      {paginationItems}
                      <Pagination.Next
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                      />
                      <Pagination.Last
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          {importResult ? 'Close' : 'Cancel'}
        </Button>
        {!importResult && (
          <Button
            variant='primary'
            onClick={handleUpload}
            disabled={isUploading || !carrierId || !asOfDate || !file}
          >
            {isUploading ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' /> Processing...
              </>
            ) : (
              'Upload & Process'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
